import type * as pf from '@amirada/lib-shared'
import type { InjectionKey } from 'vue'
import { useCartStore } from '@base/stores/cartStore'
import type { ECarts } from '@base/utils/constants'
import { ECartUserDefined } from '~/utils/constants'
import { ut_wilo_cartContent } from '~/utils/priceList'

/*
 * The rows of the price-list cart — where the same series may stand more than once (24.09.2026).
 *
 * Every other cart holds an object at most once, and the base store is built on that: it finds a row
 * by its namePath, skips a namePath that is already there (setAddListToCart), counts it up instead of
 * adding it (addCartItem with an amount) and removes every row that contains the config name. In the
 * price list a series is its own object each time it is placed — once under two chapters is two rows,
 * two pages in the PDF, each with its own price tables and its own hidden flag (Christoph). So a row
 * carries its own id, `userDefined.rid`, and everything here addresses the ROW by it.
 *
 * ⚑⚑ For the price list, never use the base cart functions that address a row by namePath
 * (setUpdateUserDefined, setRemoveItemFromCart, setRemoveListFromCart, setAddListToCart,
 * setAddItemToCart) — they would hit the first copy, or skip the second, silently. Kept here, in the
 * client, on purpose: it is this customer's rule, not the base's (decision 24.09.2026). The one thing
 * the base does for it is keep the copies apart when it refreshes (updateCartItemsInfo, one answer
 * per row).
 *
 * The id lives in userDefined because that is what survives everything a row goes through: stripCart
 * keeps it, the server echoes it back per row (cmdCart), and the project saves it. Markers need none,
 * their `local:` namePath is already unique — rowKey falls back to the namePath for them.
 *
 * ⚑ These write the cart with `setCart`, which does not touch local storage. That is right for this
 * cart only: it is tab-local and never stored there (tabLocalCarts).
 */
/*
 * What identifies a cart row: its own id, or — for a marker, a row from before the id existed, and
 * every row of the other carts, which never get one — its namePath. Usable for any cart for exactly
 * that reason (Cart.vue sorts every cart with it).
 */
export function cartRowKey(item: pf.CartItem): string {
  return item.userDefined?.[ECartUserDefined.cart_us_rowId] || item.namePath
}

export function usePriceListCart(cart: ECarts) {
  const cartStore = useCartStore()
  const rowKey = cartRowKey

  // Give every series row that has none an id. Rows passed in are changed in place and returned.
  function withRowIds(items: pf.CartItem[]): pf.CartItem[] {
    for (const item of items) {
      if (ut_wilo_cartContent(item) || item.userDefined?.[ECartUserDefined.cart_us_rowId]) { continue }
      item.userDefined = { ...(item.userDefined || {}), [ECartUserDefined.cart_us_rowId]: crypto.randomUUID() }
    }
    return items
  }

  // After a project was opened or a shared list loaded: rows saved before the id existed get one.
  function ensureRowIds(): void {
    withRowIds(cartStore.getCart(cart))
  }

  /*
   * Insert rows — series as `{ namePath }`, markers complete — as NEW rows at a position, or at the end
   * without one. Nothing is skipped: a series already in the list is placed again. The series are
   * resolved in one request; the markers go through untouched and everything keeps its order.
   */
  async function insertRows(rows: pf.CartItem[], position?: number): Promise<void> {
    if (!rows.length) { return }
    const resolved: pf.CartItem[] = await cartStore.updateCartItemsInfo(withRowIds(rows))
    const list: pf.CartItem[] = [...cartStore.getCart(cart)]
    const at: number = position === undefined || position < 0 || position > list.length ? list.length : position
    list.splice(at, 0, ...resolved)
    cartStore.setCart(cart, list)
  }

  // Set one userDefined field on exactly this row. `undefined` leaves the key without a value, which
  // JSON drops — a row toggled back looks like one never touched.
  function setRowUserDefined(item: pf.CartItem, key: ECartUserDefined, value: unknown): void {
    const row: pf.CartItem | undefined = cartStore.getCart(cart).find(i => rowKey(i) === rowKey(item))
    if (!row) { return }
    row.userDefined = { ...(row.userDefined || {}), [key]: value }
  }

  // Remove exactly these rows — no other copy of the same series, and no products (a price list has
  // none: series and structure only).
  function removeRows(items: pf.CartItem[]): void {
    const keys: Set<string> = new Set(items.map(rowKey))
    cartStore.setCart(cart, cartStore.getCart(cart).filter(i => !keys.has(rowKey(i))))
  }

  return { rowKey, ensureRowIds, insertRows, setRowUserDefined, removeRows }
}

/*
 * The selection of the catalog modal (CartCatalogModal → CatalogView → List → CartIcons).
 *
 * The modal starts EMPTY and marks what is picked; only "accept" puts it into the cart, as new rows —
 * it knows nothing of what the list already holds, so it can neither show nor remove it (Christoph,
 * 24.09.2026). Provided by the modal and injected by the tiles, so nothing in between has to carry it.
 * Order = order of the clicks.
 */
export interface IPriceListSelection {
  isSelected: (namePath: string) => boolean
  toggle: (namePath: string) => void
  add: (namePaths: string[]) => void
}

export const priceListSelectionKey: InjectionKey<IPriceListSelection> = Symbol('priceListSelection')
